import {
	AddOutlined,
	ChecklistOutlined,
	CheckOutlined,
	ErrorOutline,
	FindInPageOutlined,
	HourglassEmptyOutlined,
	NoteAddOutlined,
	UploadFileOutlined,
} from "@mui/icons-material";
import {
	Button,
	IconButton,
	Link,
	Skeleton,
	Stack,
	TableHead,
	Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { useState } from "react";

import { useRequiredDocumentsQuery } from "AdvisoryManagement/Api/Queries/useRequiredDocumentsQuery";
import { AddDocument } from "AdvisoryManagement/Components/Documents/AddDocument";
import { DocumentDetailDialog } from "AdvisoryManagement/Components/Documents/DocumentDetailDialog";
import type { DummyDocumentData } from "AdvisoryManagement/types";

type ContractDocumentsTableProps = {
	contractId: string;
	documents: DummyDocumentData[];
	open: boolean;
};

export const ContractDocumentsTable = ({
	contractId,
	documents,
	open,
}: ContractDocumentsTableProps) => {
	const { data: requiredDocuments, isLoading } = useRequiredDocumentsQuery(
		contractId,
		{ enabled: open },
	);

	const [openedDialog, setOpenedDialog] = useState<null | string>(null);
	const [addDocumentOpen, setAddDocumentOpen] = useState<string | null>(null);

	const missingDocuments =
		requiredDocuments?.filter(
			(req) =>
				!documents.some((doc) => doc.documentType === req.documentTypeCode),
		) ?? [];

	const extendedDocuments = documents.map((doc) => {
		const req = requiredDocuments?.find(
			(req) => req.documentTypeCode === doc.documentType,
		);
		return { ...doc, requiredDocument: req };
	});

	const hasContent = documents.length > 0 || missingDocuments.length > 0;

	return (
		<TableRow sx={{ backgroundColor: "#f9f9f9" }}>
			<TableCell
				sx={{
					padding: "0 !important",
				}}
				colSpan={5}
			>
				<Collapse in={open} timeout={400} unmountOnExit>
					<Box padding={0}>
						{isLoading ? (
							<DocumentsTableSkeleton />
						) : (
							<Table
								size="small"
								sx={{
									"& .MuiSvgIcon-root": {
										display: "grid",
										placeContent: "center",
									},
								}}
							>
								{hasContent && (
									<TableHead>
										<TableRow>
											<TableCell sx={{ width: 70 }} />
											<TableCell>Dokument</TableCell>
											<TableCell>Soubor</TableCell>
											<TableCell sx={{ textAlign: "center" }}>
												Kontrola
											</TableCell>
											<TableCell sx={{ textAlign: "center" }}>
												Průvodka
											</TableCell>
											<TableCell sx={{ textAlign: "center" }}>
												Rychlé akce
											</TableCell>
										</TableRow>
									</TableHead>
								)}
								<TableBody>
									{extendedDocuments.map((doc) => (
										<TableRow key={doc.documentId}>
											<TableCell />
											<TableCell component="th" scope="row">
												{doc.requiredDocument?.documentTypeName ||
													doc.documentType}
											</TableCell>
											<TableCell>
												{doc.status !== "New" ? (
													<Link
														component="button"
														onClick={() => setOpenedDialog(doc.documentId)}
														sx={{
															textDecoration: "none",
															color: "primary.main",
															fontWeight: 500,
															fontSize: "0.75rem",
															"&:hover": {
																textDecoration: "underline",
															},
														}}
													>
														{doc.documentName || "Dokument"}
													</Link>
												) : (
													<Stack direction="row" spacing={0.5}>
														<ErrorOutline color="disabled" fontSize="small" />
														<Typography variant="caption" color="textDisabled">
															Nenahráno
														</Typography>
													</Stack>
												)}
											</TableCell>
											<StatusCell status={doc.controlStatus} />
											<StatusCell status={doc.printStatus} />
											<ActionsCell document={doc} />
										</TableRow>
									))}
									{missingDocuments.length > 0 && (
										<TableRow sx={{ backgroundColor: "#FAFAFA" }}>
											<TableCell />
											<TableCell
												colSpan={5}
												sx={{
													borderBottom: "none",
													pt: 2,
													pb: 0.5,
												}}
											>
												<Typography variant="h6">
													Požadované dokumenty
												</Typography>
											</TableCell>
										</TableRow>
									)}
									{missingDocuments.map((req) => (
										<TableRow key={req.documentTypeCode}>
											<TableCell />
											<TableCell component="th" scope="row">
												<Typography variant="body2" color="text.secondary">
													{req.documentTypeName}
												</Typography>
											</TableCell>
											<TableCell colSpan={3} />
											<TableCell
												sx={{ display: "grid", justifyItems: "center" }}
											>
												<Button
													size="small"
													startIcon={<AddOutlined />}
													onClick={() =>
														setAddDocumentOpen(req.documentTypeCode)
													}
												>
													Nahrát soubor
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
						{openedDialog && (
							<DocumentDetailDialog
								documentId={openedDialog}
								onClose={() => setOpenedDialog(null)}
							/>
						)}
						{addDocumentOpen !== null && (
							<AddDocument
								contractId={contractId}
								defaultDocumentType={addDocumentOpen}
								disableDocumentType
								onClose={() => setAddDocumentOpen(null)}
							/>
						)}
					</Box>
				</Collapse>
			</TableCell>
		</TableRow>
	);
};

const SKELETON_ROWS = 3;
const SKELETON_CELLS = [120, 100, 60, 60, 60];

const DocumentsTableSkeleton = () => (
	<Table size="small">
		<TableBody>
			{Array.from({ length: SKELETON_ROWS }).map((_, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder
				<TableRow key={i}>
					<TableCell sx={{ width: 70 }} />
					{SKELETON_CELLS.map((width, j) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder
						<TableCell key={j}>
							<Skeleton variant="text" width={width} />
						</TableCell>
					))}
				</TableRow>
			))}
		</TableBody>
	</Table>
);

type StatusCellProps = {
	status: "Ready" | "Pending" | "NotReady";
	text?: string;
};

const StatusCell = ({ status, text }: StatusCellProps) => {
	const getStatusDisplay = () => {
		switch (status) {
			case "Ready":
				return {
					icon: <CheckOutlined fontSize="small" />,
					text: "Hotovo",
					color: "success.main",
				};
			case "Pending":
				return {
					icon: <HourglassEmptyOutlined fontSize="small" />,
					text: "Probíhá",
					color: "warning.main",
				};
			case "NotReady":
				return {
					icon: <ErrorOutline fontSize="small" />,
					text: "Nevytvořeno",
					color: "text.disabled",
				};
		}
	};

	const { icon, text: generalText, color } = getStatusDisplay();

	return (
		<TableCell>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="center"
				spacing={0.5}
			>
				<Box sx={{ color, display: "flex", alignItems: "center" }}>{icon}</Box>
				<Typography variant="caption" sx={{ color }}>
					{text || generalText}
				</Typography>
			</Stack>
		</TableCell>
	);
};

type ActionsCellProps = {
	document: DummyDocumentData;
};

const ActionsCell = ({ document }: ActionsCellProps) => {
	const [dialogOpen, setDialogOpen] = useState(false);

	return (
		<TableCell sx={{ justifyItems: "center" }}>
			<Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
				{document.status === "New" && (
					<IconButton
						size="small"
						onClick={() => {}}
						title="Nahrát dokument"
						sx={{ color: "action.active" }}
					>
						<UploadFileOutlined fontSize="small" />
					</IconButton>
				)}
				{document.controlStatus === "NotReady" && (
					<IconButton
						size="small"
						onClick={() => {}}
						title="Spustit kontrolu"
						sx={{ color: "action.active" }}
					>
						<ChecklistOutlined fontSize="small" />
					</IconButton>
				)}
				{document.printStatus === "NotReady" && (
					<IconButton
						size="small"
						onClick={() => {}}
						title="Vytvořit tiskovou sestavu"
						sx={{ color: "action.active" }}
					>
						<NoteAddOutlined fontSize="small" />
					</IconButton>
				)}
				<IconButton
					size="small"
					onClick={() => setDialogOpen(true)}
					title="Zobrazit detaily"
					sx={{ color: "action.active" }}
				>
					<FindInPageOutlined fontSize="small" />
				</IconButton>
				{dialogOpen && (
					<DocumentDetailDialog
						documentId={document.documentId}
						onClose={() => setDialogOpen(false)}
					/>
				)}
			</Box>
		</TableCell>
	);
};
